import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Briefcase, MapPin, House, CurrencyDollar, ArrowSquareOut, MagnifyingGlass, Buildings, TrendUp } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSalary = (j) => {
  if (j.salary_range) return j.salary_range;
  if (j.salary_min) return `$${Math.round(j.salary_min / 1000)}K+`;
  return null;
};

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-4 flex items-center gap-3">
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent}`}><Icon size={18} weight="duotone" /></div>
    <div className="min-w-0">
      <div className="text-lg font-extrabold text-zinc-900 leading-none">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  </div>
);

const JobCard = ({ j }) => {
  const sal = fmtSalary(j);
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-5 hover:border-violet-300 hover:shadow-[0_4px_24px_-12px_rgba(139,92,246,0.4)] transition-all" data-testid={`job-card-${j.job_id}`}>
      <div className="flex items-start gap-3 mb-3">
        {j.company_logo
          ? <img src={j.company_logo} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
          : <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 font-bold flex items-center justify-center flex-shrink-0">{(j.company || "?")[0]}</div>}
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-zinc-900 leading-tight truncate">{j.title}</h3>
          <div className="text-sm text-zinc-600 truncate">{j.company} {j.yc_batch && <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded ml-1">{j.yc_batch}</span>}</div>
        </div>
      </div>
      {j.company_tagline && <p className="text-xs text-zinc-500 line-clamp-1 mb-3">{j.company_tagline}</p>}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {j.role_category && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{j.role_category}</span>}
        {j.remote && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-0.5"><House size={9} weight="fill" /> Remote</span>}
        {j.job_type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{j.job_type}</span>}
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-zinc-500 mb-3">
        <span className="inline-flex items-center gap-1 truncate"><MapPin size={12} /> {j.primary_location || "—"}</span>
        {sal && <span className="inline-flex items-center gap-0.5 font-bold text-emerald-700 flex-shrink-0"><CurrencyDollar size={12} weight="bold" />{sal}</span>}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-zinc-400">{j.posted_ago ? `${j.posted_ago} ago` : ""}</span>
        <a href={j.apply_url || j.url} target="_blank" rel="noopener noreferrer" data-testid={`job-apply-${j.job_id}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900">
          View role <ArrowSquareOut size={12} weight="bold" />
        </a>
      </div>
    </div>
  );
};

export default function JobsBoard() {
  const [facets, setFacets] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [remote, setRemote] = useState(false);
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const PAGE = 24;

  useEffect(() => { axios.get(`${API}/jobs/facets`).then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/jobs`, { params: { q: q || undefined, role, remote: remote || undefined, sort, page, page_size: PAGE } })
      .then((r) => { setJobs(r.data.jobs || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [q, role, remote, sort, page]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setPage(1); }, [q, role, remote, sort]);

  const pages = Math.ceil(total / PAGE);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="jobs-board-view">
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2 inline-flex items-center gap-1.5"><Briefcase size={13} weight="fill" /> YC JOBS · HIRING SIGNAL</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Who's hiring — and for what.</h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        Live "Work at a Startup" roles from Y Combinator companies. Hiring is a real-time momentum signal — where startups spend on talent is where money &amp; growth are flowing.
      </p>

      {/* Insight strip */}
      {facets && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="jobs-insight-strip">
          <Stat label="open roles" value={facets.total} icon={Briefcase} accent="bg-violet-50 text-violet-600" />
          <Stat label="remote-friendly" value={`${facets.remote_pct}%`} icon={House} accent="bg-emerald-50 text-emerald-600" />
          <Stat label="median base salary" value={facets.median_salary_min ? `$${Math.round(facets.median_salary_min / 1000)}K` : "—"} icon={CurrencyDollar} accent="bg-amber-50 text-amber-600" />
          <Stat label="top hiring role" value={facets.roles?.[0]?.role || "—"} icon={TrendUp} accent="bg-blue-50 text-blue-600" />
        </div>
      )}

      {/* Top hiring companies */}
      {facets?.top_companies?.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="jobs-top-companies">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 inline-flex items-center gap-1"><Buildings size={13} /> Most hiring:</span>
          {facets.top_companies.slice(0, 6).map((c) => (
            <button key={c.company} onClick={() => setQ(c.company)} className="text-xs px-2.5 py-1 rounded-full bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300">
              {c.company} <b className="text-violet-600">{c.count}</b>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="mt-6 mb-5 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="jobs-search" placeholder="Search role or company…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 text-sm focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
          </div>
          <div className="flex gap-2">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="jobs-sort" className="px-3 py-2.5 rounded-xl border border-zinc-200 text-sm bg-white">
              <option value="recent">Most recent</option>
              <option value="salary">Highest salary</option>
            </select>
            <button onClick={() => setRemote(!remote)} data-testid="jobs-remote-toggle"
              className={`px-3.5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${remote ? "bg-emerald-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-emerald-300"}`}>
              🏠 Remote only
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="jobs-role-filters">
          <button onClick={() => setRole("all")} className={`text-xs font-semibold px-3 py-1.5 rounded-full ${role === "all" ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>All roles</button>
          {(facets?.roles || []).map((r) => (
            <button key={r.role} onClick={() => setRole(r.role)} data-testid={`jobs-role-${r.role}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${role === r.role ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
              {r.role} <span className={role === r.role ? "text-violet-200" : "text-zinc-400"}>{r.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading && jobs.length === 0 ? (
        <div className="text-zinc-400 text-sm py-16 text-center">Loading roles…</div>
      ) : jobs.length === 0 ? (
        <div className="text-zinc-500 text-sm py-16 text-center">No roles match your filters.</div>
      ) : (
        <>
          <div className="text-xs text-zinc-400 mb-3">{total.toLocaleString()} roles</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((j) => <JobCard key={j.job_id} j={j} />)}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="jobs-prev" className="px-4 py-2 rounded-full border border-zinc-200 text-sm font-semibold disabled:opacity-40">← Prev</button>
              <span className="text-sm text-zinc-500">Page {page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="jobs-next" className="px-4 py-2 rounded-full border border-zinc-200 text-sm font-semibold disabled:opacity-40">Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
